'use client'
import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react'
import { useLanguage } from './LanguageContext'
import { useAuth } from './AuthContext'
import { useUI } from './UIContext'

import { BUILTIN_CONFIGS } from '@/constants/builtinConfigs'
import { storageService } from '@/utils/storageService'
import { generateConfigString as generateConfigStringUtil } from '@/utils/configGenerator'
import { getBridge } from '@/utils/bridge'
import { track } from '@/utils/analytics'

const ConfigContext = createContext()

export const useConfig = () => useContext(ConfigContext)

const DEFAULT_CONFIG_DATA = {
    crosshair: {
        cl_crosshairsize: 5,
        cl_crosshairthickness: 0.5,
        cl_crosshairgap: 0,
        cl_crosshairalpha: 255,
        cl_crosshaircolor_r: 0,
        cl_crosshaircolor_g: 255,
        cl_crosshaircolor_b: 0,
        cl_crosshairdot: 0,
        cl_crosshair_drawoutline: 0,
        cl_crosshair_outlinethickness: 0.5,
        cl_crosshairstyle: 4,
        cl_crosshair_recoil: 0,
        cl_crosshair_t: 0,
        cl_crosshair_friendly_warning: 1,
        cl_crosshair_sniper_width: 1,
        presets: [],
        bind_toggle_presets: ''
    },
    mouse: {
        sensitivity: 1.2,
        zoom_sensitivity_ratio: 1.0,
        m_rawinput: true,

        m_yaw: 0.022,
        m_pitch: 0.022,
        dpi: 800,
        aspectRatio: '16:9'
    },
    viewmodel: {
        viewmodel_offset_x: 1,
        viewmodel_offset_y: 1,
        viewmodel_offset_z: -1,
        viewmodel_fov: 60,
        cl_prefer_lefthanded: 0
    },
    hud: {
        cl_hud_color: 1, // 1 = Soft White
        hud_scaling: 0.9,
        cl_radar_scale: 0.7,
        cl_hud_radar_scale: 1.0,
        cl_radar_always_centered: 1,
        cl_radar_rotate: 1,
        cl_showloadout: 1,
        cl_radar_icon_scale_min: 0.6,
        cl_teammate_colors_show: 1,
        cl_radar_opacity: 0.7
    },
    audio: {},
    binds: {
        // Movement
        key_forward: '',
        key_back: '',
        key_left: '',
        key_right: '',
        key_walk: '',
        key_duck: '',
        key_jump: '',

        // Weapons & Combat
        key_attack: '',
        key_attack2: '',
        key_reload: '',
        key_use: '',
        key_drop: '',
        key_inspect: '',
        key_lastinv: '', // Q
        key_buymenu: '', // B
        key_autobuy: '', // F3
        key_rebuy: '', // F4

        // Weapon Slots
        key_slot1: '', // Primary weapon
        key_slot2: '', // Secondary weapon
        key_slot3: '', // Knife/Melee
        key_slot4: '', // Grenades (cycle)
        key_slot5: '', // Bomb (T side)

        // Grenades & Slots
        grenade_flash: '',
        grenade_smoke: '',
        grenade_molotov: '',
        grenade_he: '',
        grenade_decoy: '',
        key_taser: '', // Zeus
        key_healthshot: '',

        // Communication
        key_mic: '',
        key_chat: '',
        key_chat_team: '',
        key_ping: '',
        key_radio1: '',
        key_radio2: '',
        key_radio3: '',
        key_radial_radio: '',
        key_radial_radio2: '',

        // UI & Misc
        key_score: '',
        key_console: '',
        key_spray: '',
        key_team_equip: '',
        key_show_loadout: '',
        key_team_menu: '',
        key_screenshot: '',
        key_quit_prompt: '',
        key_sell_back_all: '',
        key_inv_next: '',
        key_inv_prev: '',
        key_cancel_select: '',

        // Specials
        jump_throw: false,
        jump_throw_key: '',
        mwheel_jump: 'none',
        bind_mute_all: '',
        bind_vol_up: '',
        bind_vol_down: '',
        bind_voice_up: '',
        bind_voice_down: '',
        bind_radar_toggle: '',
        clear_decals: false,
        viewmodel_switchhands_bind: 'mouse5',
        grenade_binds_buy: true, // Auto-buy grenades with same key

        // Custom Buy Binder
        custom_binds: [
            { id: 'def_full_buy', name: 'Full Buy (AK/M4)', key: '', items: ['ak47', 'm4a1_silencer', 'vesthelm', 'defuser'] },
            { id: 'def_full_nades', name: 'Full Granadas', key: '', items: ['smokegrenade', 'hegrenade', 'flashbang', 'molotov', 'incgrenade'] },
            { id: 'def_awp', name: 'Full AWP', key: '', items: ['awp', 'vesthelm', 'defuser'] },
            { id: 'def_force_smg', name: 'Force SMG', key: '', items: ['mac10', 'mp9', 'vesthelm'] },
            { id: 'def_force_rifle', name: 'Force Rifle', key: '', items: ['galilar', 'famas', 'vesthelm'] },
            { id: 'def_deagle', name: 'Eco Deagle', key: '', items: ['deagle', 'vest'] },
            { id: 'def_p250', name: 'Eco P250', key: '', items: ['p250', 'vest'] },
            { id: 'def_scout', name: 'Scout Buy', key: '', items: ['ssg08', 'vesthelm'] },
            { id: 'def_xm1014', name: 'Auto Shotgun', key: '', items: ['xm1014', 'vesthelm'] }
        ],
        radar_big_key: 'q',
        radar_overview_key: ''
    },
    custom_scripts: {
        raw_content: ''
    },
    graphics: {
        r_drawtracers_firstperson: true,
        r_dynamic: true,
        cl_allow_animated_avatars: true,
        engine_low_latency_sleep_after_client_tick: true,
        fps_max: 0,
        cl_ragdoll_workaround_threshold: true
    },
    video: {
        resolution_width: 1920,
        resolution_height: 1080,
        refresh_rate: 144,
        fullscreen: true,
        aspect_ratio: '16:9'
    },
    practice: {
        sv_cheats: true,
        infinite_resources: true,
        grenade_utils: true,
        server_optimization: true,
        bot_freeze: false,
        auto_heal: false,
        radar_full: false,
        wallbang_info: false,
        auto_bhop: false,
        bind_noclip: 'alt',
        bind_rethrow: 'p',
        bind_bot_place: 'j',
        bind_bot_add_t: '',
        bind_bot_add_ct: '',
        bind_bot_kick: '',
        bind_timescale: '',
        bind_restart_round: '',
        bind_save_pos: '',
        bind_load_pos: '',
        bind_clear_util: '',
        bind_toggle_impacts: '',
        bind_prac_alias: ''
    },
    launch_options: {
        novid: true,
        high: true,
        console: true,
        fullscreen: false,
        width: 1920,
        height: 1080,
        refresh: 144,
        exec_autoexec: true,
        custom_args: ''
    },
    settings: {
        // Default Radio Wheel (Tab 0)
        "cl_radial_radio_tab_0_text_1": "\"#Chatwheel_enemyfolks\"",
        "cl_radial_radio_tab_0_text_2": "\"#Chatwheel_needbackup\"",
        "cl_radial_radio_tab_0_text_3": "\"#Chatwheel_holdposition\"",
        "cl_radial_radio_tab_0_text_4": "\"#Chatwheel_sectorclear\"",
        "cl_radial_radio_tab_0_text_5": "\"#Chatwheel_fallback\"",
        "cl_radial_radio_tab_0_text_6": "\"#Chatwheel_affirmative\"",
        "cl_radial_radio_tab_0_text_7": "\"#Chatwheel_negative\"",
        "cl_radial_radio_tab_0_text_8": "\"#Chatwheel_requestdrop\""
    }
}

const CS2_DEFAULT_BINDS = {
    // Movement
    key_forward: 'w',
    key_back: 's',
    key_left: 'a',
    key_right: 'd',
    key_walk: 'shift',
    key_duck: 'ctrl',
    key_jump: 'space',

    // Weapons & Combat
    key_attack: 'mouse1',
    key_attack2: 'mouse2',
    key_reload: 'r',
    key_use: 'e',
    key_drop: 'g',
    key_inspect: 'f',
    key_lastinv: 'q',
    key_buymenu: 'b',
    key_autobuy: 'f3',
    key_rebuy: 'f4',

    // Grenades & Slots - Defaults use '4' to cycle, slots are 6-10 usually not bound to specific keys by default but numbers 1-5 for weapons
    grenade_flash: '',
    grenade_smoke: '',
    grenade_molotov: '',
    grenade_he: '',
    grenade_decoy: '',
    key_taser: '',
    key_healthshot: '',

    // Communication
    key_mic: 'k', // Often K or V depending on version, K is classic default
    key_chat: 'y',
    key_chat_team: 'u',
    key_ping: 'mouse3', // Middle mouse usually
    key_radio1: 'z',
    key_radio2: 'x',
    key_radio3: 'c',

    // UI & Misc
    key_score: 'tab',
    key_console: '`',
    key_spray: 't',
    key_team_equip: '', // Not bound by default usually
    key_show_loadout: 'i',
    key_team_menu: 'm',
    key_screenshot: 'f5',
    key_quit_prompt: 'f10',
    key_sell_back_all: 'del',
    key_inv_next: 'mwheeldown',
    key_inv_prev: 'mwheelup',
    key_cancel_select: 'escape',
    key_radial_radio: 'c',
    key_radial_radio2: 'v',
    key_radio1: 'z',
    key_radio2: 'x',
    key_radio3: '', // C is now Radial Radio, so clear old Radio3 default or it will conflict if user uses defaults
    key_taser: '', // User list doesn't explicitly bind Zeus, but slot11 is standard.
    key_healthshot: 'x', // User list has 'x' as slot12.
    key_mic: 'mouse4',

    // Specials (Custom Config-Strike features)
    jump_throw: false,
    jump_throw_key: '',
    mwheel_jump: 'none',
    bind_mute_all: '',
    bind_vol_up: '',
    bind_vol_down: '',
    clear_decals: false,
    custom_binds: [
        { id: 'def_full_buy', name: 'Full Buy (AK/M4)', key: '', items: ['ak47', 'm4a1_silencer', 'vesthelm', 'defuser'] },
        { id: 'def_full_nades', name: 'Full Granadas', key: '', items: ['smokegrenade', 'hegrenade', 'flashbang', 'molotov', 'incgrenade'] },
        { id: 'def_awp', name: 'Full AWP', key: '', items: ['awp', 'vesthelm', 'defuser'] },
        { id: 'def_force_smg', name: 'Force SMG', key: '', items: ['mac10', 'mp9', 'vesthelm'] },
        { id: 'def_force_rifle', name: 'Force Rifle', key: '', items: ['galilar', 'famas', 'vesthelm'] },
        { id: 'def_deagle', name: 'Eco Deagle', key: '', items: ['deagle', 'vest'] },
        { id: 'def_p250', name: 'Eco P250', key: '', items: ['p250', 'vest'] },
        { id: 'def_scout', name: 'Scout Buy', key: '', items: ['ssg08', 'vesthelm'] },
        { id: 'def_xm1014', name: 'Auto Shotgun', key: '', items: ['xm1014', 'vesthelm'] }
    ]
}

const PRO_PRESETS = {
    's1mple': {
        ...CS2_DEFAULT_BINDS,
        key_jump: 'mwheeldown', // He often uses both or just mwheel
        jump_throw: true,
        jump_throw_key: 'mouse4', // Common for him
        key_lastinv: 'q',
        key_console: '`'
    },
    'NiKo': {
        ...CS2_DEFAULT_BINDS,
        key_jump: 'mwheeldown',
        grenade_smoke: 'x',
        grenade_molotov: 'c',
        grenade_flash: 'f',
        grenade_he: 'z',
        key_inspect: 'h'
    },
    'm0NESY': {
        ...CS2_DEFAULT_BINDS,
        mwheel_jump: 'both', // Wheel up and down
        key_ping: 'mouse3',
        grenade_smoke: 'x',
        grenade_flash: 'c',
        grenade_molotov: 'v',
        key_use: 'e'
    },
    'ZywOo': {
        ...CS2_DEFAULT_BINDS,
        key_jump: 'mwheeldown',
        key_duck: 'ctrl',
        key_walk: 'shift',
        key_lastinv: 'q'
    },
    'JACKZ': {
        ...CS2_DEFAULT_BINDS,
        key_forward: 'uparrow',
        key_back: 'downarrow',
        key_left: 'leftarrow',
        key_right: 'rightarrow',
        key_attack: 'mouse1',
        key_zoom: 'mouse2',
        key_jump: 'space', // Classic arrow player usually uses space or numpad 0
        key_duck: 'shift', // Common variation for arrow players
        key_walk: 'ctrl',
        grenade_smoke: 'del',
        grenade_flash: 'end',
        grenade_molotov: 'pagedown'
    },
    'Mezii': {
        ...CS2_DEFAULT_BINDS,
        key_forward: 'mouse2',
        key_back: 'space',
        key_left: 's',
        key_right: 'd',
        key_jump: 'a', // Jumping with A
        key_attack2: 'w', // Zoom with W? He needs another key since mouse2 is move
        key_duck: 'ctrl',
        key_walk: 'shift'
    },
    'donk': {
        ...CS2_DEFAULT_BINDS,
        key_jump: 'mwheeldown',
        key_duck: 'ctrl',
        key_walk: 'shift',
        // Donk uses very standard binds, often unbinds unnecessary things.
        // We keep defaults for most to simulate his lean config.
        jump_throw: false // He manually does it or uses legal binds
    },
    'ropz': {
        ...CS2_DEFAULT_BINDS,
        key_jump: 'mwheeldown',
        key_lastinv: 'q',
        grenade_smoke: 'z',
        grenade_flash: 'x',
        grenade_molotov: 'c',
        grenade_he: 'v'
    }
}

export const ConfigProvider = ({ children }) => {
    const { t } = useLanguage()
    const { isAuthenticated } = useAuth()
    const { showPrompt } = useUI()

    // Active modules (e.g., ['crosshair', 'mouse'])
    const [activeModules, _setActiveModules] = useState([])
    const activeModulesRef = useRef([])

    const setActiveModules = (valOrFn) => {
        _setActiveModules(prev => {
            const newVal = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn
            activeModulesRef.current = newVal
            return newVal
        })
    }
    const [isSaving, setIsSaving] = useState(false)
    const [hasUnsyncedChanges, setHasUnsyncedChanges] = useState(false)
    const saveTimeoutRef = React.useRef(null)

    // Config data keyed by module name
    const [configData, setConfigData] = useState(DEFAULT_CONFIG_DATA)
    const configDataRef = useRef(DEFAULT_CONFIG_DATA)

    // Ensure ref is always in sync with render cycle
    // We update it in modify functions usually, but also sync here for external updates
    useEffect(() => {
        configDataRef.current = configData
    }, [configData])

    // Only watch what would actually be written to the .cfg — changes in inactive modules are ignored
    const cfgOutputForActiveModules = React.useMemo(() =>
        generateConfigStringUtil(configData, activeModules || []),
        [configData, activeModules]
    )

    useEffect(() => {
        if (!configName || !initialLoadDoneRef.current || isLoadingRef.current) return
        setHasUnsyncedChanges(true)
    }, [cfgOutputForActiveModules])

    const [toggleSignal, setToggleSignal] = useState({ id: 1, expand: true })
    const [focusSignal, setFocusSignal] = useState({ id: 0, moduleKey: null, expand: true })
    const [configName, setConfigName] = useState('')
    const [configId, setConfigId] = useState(null) // Nuevo estado para ID
    const [configIsLocked, _setConfigIsLocked] = useState(false)

    const setConfigIsLocked = (val) => {
        configIsLockedRef.current = val
        _setConfigIsLocked(val)
    }

    const [loadSignal, setLoadSignal] = useState(0) // Señal de que se cargó una config
    const [lockedFeedbackSignal, setLockedFeedbackSignal] = useState(0)
    const isHandlingLockedPrompt = React.useRef(false)
    const syncDebounceRef = React.useRef(null)
    const isSyncingRef = React.useRef(false)

    // Telemetría anónima (ver utils/analytics.js): dedup por sesión para no
    // spamear module_edit en cada tecla, y configs_count una sola vez por carga.
    const editedModulesTrackedRef = React.useRef(new Set())
    const configsCountTrackedRef = React.useRef(false)
    const trackModuleEdit = (moduleName) => {
        if (!moduleName || editedModulesTrackedRef.current.has(moduleName)) return
        editedModulesTrackedRef.current.add(moduleName)
        track('module_edit', { module: moduleName })
    }
    const trackConfigsCount = (n) => {
        if (configsCountTrackedRef.current) return
        configsCountTrackedRef.current = true
        track('configs_count', { n })
    }

    // Sync state for CS2
    const [syncStatus, setSyncStatus] = useState(() => {
        if (typeof window !== 'undefined' && localStorage.getItem('cs2_install_path')) {
            return 'synced'
        }
        return 'idle'
    }) // 'idle', 'syncing', 'synced', 'error'
    const [lastSyncTime, setLastSyncTime] = useState(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('last_sync_time')
            return saved ? new Date(saved) : null
        }
        return null
    })

    useEffect(() => {
        if (lastSyncTime) {
            localStorage.setItem('last_sync_time', lastSyncTime.toISOString())
        }
    }, [lastSyncTime])

    // CS2 Path Detection
    const [cs2Path, setCs2Path] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('cs2_install_path')
        }
        return null
    })
    const [isDetecting, setIsDetecting] = useState(false)

    const handleDetectPath = async () => {
        setIsDetecting(true)
        try {
            const path = await getBridge()?.detectCS2Path()
            if (path) {
                setCs2Path(path)
                localStorage.setItem('cs2_install_path', path)
                // Trigger sync after path update
                syncConfigsToGame()
                return path
            } else {
                alert("No se pudo detectar automáticamente la carpeta de CS2.\nAsegúrate de que Steam esté instalado y CS2 actualizado.")
            }
        } catch (e) {
            console.error(e)
        } finally {
            setIsDetecting(false)
        }
        return null
    }

    const handleManualPathSelect = async () => {
        try {
            const path = await getBridge()?.selectFolder();
            if (path) {
                setCs2Path(path);
                localStorage.setItem('cs2_install_path', path);
                // Trigger sync after manual path update
                syncConfigsToGame();
            }
        } catch (e) {
            console.error('Error selecting folder:', e);
        }
    };

    // State to track if changes are made to avoid auto-saving on initial load
    const isDirtyRef = React.useRef(false)
    const isLoadingRef = React.useRef(false) // NEW: Track if we're loading a config
    const configIsLockedRef = React.useRef(false)
    const configNameRef = React.useRef('')
    const initialLoadDoneRef = React.useRef(false) // Track if initial load is done

    const [userConfigs, setUserConfigs] = useState([])
    const [isLoadingConfigs, setIsLoadingConfigs] = useState(false)

    // Restore configs from cache
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const cached = localStorage.getItem('user_configs_cache_v2')
            if (cached) {
                try {
                    const cachedData = JSON.parse(cached)
                    const normalized = cachedData.map(c => ({
                        ...c,
                        isLocked: !!c.settings?._meta?.isLocked
                    }))

                    // Strictly filter out anything that looks like a builtin config
                    // The user requested to remove system configs from the flow entirely for now.
                    const userParts = normalized.filter(c => !c.isBuiltin && !String(c.id).startsWith('builtin-') && c.name !== 'autoexec')
                        .sort((a, b) => (a.id > b.id ? 1 : -1))

                    setUserConfigs(userParts)
                } catch (e) { console.error(e) }
            }
        }
    }, [])

    const fetchUserConfigs = async (silent = false) => {
        try {
            if (!silent) setIsLoadingConfigs(true)

            // Use Storage Service
            const configs = await storageService.getConfigs()

            // Si no hay configs Y es la carga inicial, crear una por defecto
            if (configs.length === 0 && !initialLoadDoneRef.current) {
                const newConfigName = "Default Config"
                const baseData = getTemplateData('default')
                const initialData = {
                    ...baseData,
                    _meta: {
                        ...baseData._meta,
                        activeModules: ['mouse', 'crosshair', 'viewmodel', 'hud', 'binds_basic', 'binds_special', 'binds_custom', 'radio']
                    }
                }

                const result = await storageService.saveConfig({
                    name: newConfigName,
                    settings: initialData
                })

                if (result.success && result.config) {
                    const defaultConfig = {
                        ...result.config,
                        isLocked: false,
                        launcherBind: '',
                        isBuiltin: false
                    }
                    setUserConfigs([defaultConfig])
                    trackConfigsCount(1)

                    // Cargar la config default inmediatamente
                    setTimeout(() => loadUserConfig(defaultConfig, true), 50)
                    localStorage.setItem('last_active_config_name', newConfigName)
                }

                return [result.config]
            } else if (configs.length === 0) {
                // Si llegamos aquí y configs.length === 0, significa que el usuario borró todo
                // durante la sesión. Simplemente devolvemos vacío sin auto-crear.
                setUserConfigs([])
                trackConfigsCount(0)
                return []
            }

            // Filter duplicates if any (legacy issue cleanup)
            const unique = []
            const seen = new Set()

            // Sort by updated_at desc
            const sorted = configs.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))

            sorted.forEach(c => {
                if (c.name === 'autoexec') return // Hide system config
                if (!seen.has(c.id)) {
                    seen.add(c.id)
                    unique.push(c)
                }
            })

            const realUserConfigs = unique.map(c => ({
                ...c,
                isLocked: !!c.settings?._meta?.isLocked,
                launcherBind: c.settings?._meta?.launcherBind || '',
                isBuiltin: false
            }))

            setUserConfigs(realUserConfigs)
            trackConfigsCount(realUserConfigs.length)
            localStorage.setItem('user_configs_cache_v2', JSON.stringify(unique))

            // AutoLoad Logic - Determinar qué config cargar
            const currentConfigName = configNameRef.current
            const isCurrentConfigValid = realUserConfigs.some(c => c.name === currentConfigName)
            let configToLoad = null

            if (!currentConfigName || !isCurrentConfigValid) {
                // Primero intentar cargar la última config activa
                const lastActive = localStorage.getItem('last_active_config_name');
                if (lastActive) {
                    const match = realUserConfigs.find(c => c.name.toLowerCase() === lastActive.toLowerCase());
                    if (match) {
                        configToLoad = match;
                    }
                }

                // Si no hay última activa, buscar autoload
                if (!configToLoad) {
                    configToLoad = realUserConfigs.find(c => c.isAutoLoad)
                }

                // Fallback: cargar la más reciente (excluyendo autoexec)
                if (!configToLoad && realUserConfigs.length > 0) {
                    const candidates = realUserConfigs.filter(c => c.name !== 'autoexec')
                    if (candidates.length > 0) {
                        configToLoad = candidates[0]
                    } else {
                        configToLoad = realUserConfigs[0]
                    }
                }
            }

            // ... (previous logic)

            if (configToLoad) {
                // console.log('[fetchUserConfigs] Loading selected config:', configToLoad.name)
                setTimeout(() => loadUserConfig(configToLoad, true), 50)
            } else if (currentConfigName && isCurrentConfigValid) {
                // Maintain current config if valid (prevent jumping to Default)
            } else if (!initialLoadDoneRef.current) {
                // FALLBACK MEJORADO: Solo si NO se ha terminado la carga inicial y no hay nada que cargar
                const existingDefault = realUserConfigs.find(c =>
                    c.name === "Default Config" || c.name === "Default"
                )

                if (existingDefault) {
                    // console.log('[fetchUserConfigs] Found existing default config, loading it:', existingDefault.name)
                    setTimeout(() => loadUserConfig(existingDefault, true), 50)
                } else {
                    // Solo si no existe ninguna, creamos la de emergencia
                    console.warn('[fetchUserConfigs] CRITICAL: No config to load and no default found. Creating emergency default.')

                    const emergencyConfig = {
                        name: "Default Config",
                        id: crypto.randomUUID(),
                        settings: {
                            ...getTemplateData('default'),
                            _meta: {
                                activeModules: ['mouse', 'crosshair', 'viewmodel', 'hud', 'binds_basic', 'binds_special', 'binds_custom', 'radio']
                            }
                        },
                        updated_at: new Date().toISOString()
                    }

                    storageService.saveConfig(emergencyConfig).then(res => {
                        if (res.success) { } // No log needed here
                    })

                    setTimeout(() => loadUserConfig(emergencyConfig, true), 50)
                }
            }

            return realUserConfigs

        } catch (error) {
            console.error('[fetchUserConfigs] Error:', error)
            setUserConfigs([])
            // En caso de error catastrófico, forzar initialLoadDone para no bloquear la UI por siempre
            initialLoadDoneRef.current = true
            return []
        } finally {
            // ... finally {
            // Fallback safety: ensure initialLoadDone becomes true after 2 seconds
            // in case something goes wrong with the load flow
            setTimeout(() => {
                if (!initialLoadDoneRef.current) {
                    console.warn('[fetchUserConfigs] Safety timeout: forcing initialLoadDone = true')
                    initialLoadDoneRef.current = true
                }
            }, 2000)
            if (!silent) setIsLoadingConfigs(false)
        }
    }

    // Initial fetch - Load for everyone (Guest or Auth)
    // Guard con ref que se setea SINCRÓNICO: en dev (StrictMode) el effect corre
    // dos veces y disparaba dos fetchUserConfigs concurrentes → ambos veían 0
    // configs y creaban "Default Config" doble, trabando guardados futuros.
    const initStartedRef = useRef(false)
    useEffect(() => {
        if (typeof window === 'undefined') return
        if (initStartedRef.current) return
        initStartedRef.current = true

        fetchUserConfigs()
    }, []) // Solo ejecutar una vez al montar

    useEffect(() => {
        // Only save if has name, AND has actual changes, AND we're not loading a config
        // CRITICAL: Also check initialLoadDoneRef to prevent saving during initial boot
        if (!configName || !isDirtyRef.current || isLoadingRef.current || !initialLoadDoneRef.current) {
            return
        }

        // Premium check for auto-save [DISABLED TO FIX AUTO-SAVE]
        /* 
        if (user && !isPremiumEffective) {
            console.log('Auto-save skip: User is not premium')
            return
        }
        */

        // console.log('Auto-save: Cambios detectados, programando guardado...')

        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)

        setIsSaving(true)

        saveTimeoutRef.current = setTimeout(async () => {
            isDirtyRef.current = false

            try {
                const result = await saveUserConfig(configName)

                if (result && result.success) {
                    if (result.id) setConfigId(result.id)
                    // NOTE: .cfg game files are NOT updated here.
                    // Use manualSaveAndSync() (the "Apply to CS2" button) to write to game folder.
                } else {
                    isDirtyRef.current = true
                }
            } catch (err) {
                console.error('Auto-save error', err)
                isDirtyRef.current = true
            } finally {
                setTimeout(() => setIsSaving(false), 500)
            }
        }, 1000)

        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
        }
    }, [configData, activeModules, configName, isAuthenticated, configIsLocked])


    useEffect(() => {
        // Enforce reset of focus signal when config context changes
        setFocusSignal({ id: 0, moduleKey: null, expand: true })
    }, [configName, loadSignal])

    const toggleModule = (moduleName) => {
        if (configIsLockedRef.current) {
            handleLockedAction(() => toggleModule(moduleName))
            return
        }
        if (!isLoadingRef.current) isDirtyRef.current = true // Only mark dirty if not loading
        setActiveModules(prev =>
            prev.includes(moduleName)
                ? prev.filter(m => m !== moduleName)
                : [...prev, moduleName]
        )
    }

    const toggleAllModules = (shouldExpand) => {
        // Not marking dirty here usually as it's UI state, but if it affects save:
        // isDirtyRef.current = true 
        setToggleSignal(prev => ({ id: prev.id + 1, expand: shouldExpand }))
    }

    const activateModules = (modulesToActivate) => {
        if (configIsLockedRef.current) {
            handleLockedAction(() => activateModules(modulesToActivate))
            return
        }
        if (!isLoadingRef.current) isDirtyRef.current = true
        setActiveModules(prev => {
            const newSet = new Set(prev)
            modulesToActivate.forEach(m => newSet.add(m))
            return Array.from(newSet)
        })
    }

    const focusModule = (moduleKey, options = { expand: true }) => {
        setFocusSignal(prev => ({ id: prev.id + 1, moduleKey, expand: options.expand }))
    }

    const clearFocusSignal = () => {
        setFocusSignal({ id: 0, moduleKey: null, expand: true })
    }

    const resetConfig = async (newName, template = 'empty', markDirty = true) => {
        // Force save previous config if it was dirty and had a name
        if (configName && isDirtyRef.current) {
            // Cancel pending auto-saves to avoid race conditions
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)

            // Await the save
            await saveUserConfig(configName)
            isDirtyRef.current = false
        }

        setConfigIsLocked(false)

        // Logic to generate unique name if not provided (empty string specifically)
        // If passed null or a specific flag, we want to clear it to return to Launcher
        let finalName = newName
        if (finalName === null) {
            finalName = '' // Clear and return to launcher
        } else if (finalName === '') {
            // Auto-generate name only if creating a NEW one
            // Refined unique name generation
            let baseName = 'Config'
            let counter = 1
            let candidate = baseName

            // Should also check locally valid configs to be safe if specific state is pending
            const existingNames = new Set(userConfigs.map(c => c.name.toLowerCase()))

            // If current name is already "Config" or "ConfigX", we might want to start from there?
            // No, strictly look for next available slot.

            while (existingNames.has(candidate.toLowerCase())) {
                candidate = `${baseName}${counter}`
                counter++
            }

            finalName = candidate
        }

        setConfigName(finalName)
        configNameRef.current = finalName
        setConfigId(null)
        isDirtyRef.current = markDirty

        setFocusSignal({ id: 0, moduleKey: null, expand: true })

        const emptyBinds = Object.keys(DEFAULT_CONFIG_DATA.binds).reduce((acc, key) => {
            const val = DEFAULT_CONFIG_DATA.binds[key]
            if (Array.isArray(val)) {
                acc[key] = []
            } else {
                acc[key] = typeof val === 'boolean' ? false : ''
            }
            return acc
        }, {})

        if (template === 'default') {
            setConfigData({
                ...DEFAULT_CONFIG_DATA,
                binds: { ...CS2_DEFAULT_BINDS }
            })
            // Activate all standard modules
            setActiveModules(['crosshair', 'mouse', 'viewmodel', 'hud', 'binds_basic', 'binds_special', 'binds_custom', 'radio'])
        } else {
            // Empty template
            setConfigData({
                ...DEFAULT_CONFIG_DATA,
                binds: emptyBinds
            })
            setActiveModules([])
        }

        // Signal that a config was "loaded" (reset) to refresh UI
        setLoadSignal(Date.now())
    }

    const duplicateConfig = async (configIdToDuplicate, customName = null) => {
        const sourceConfig = userConfigs.find(c => c.id === configIdToDuplicate)
        if (!sourceConfig) return

        let newName

        if (customName) {
            newName = customName.trim()
            if (userConfigs.some(c => c.name.toLowerCase() === newName.toLowerCase())) {
                // Fallback or error if name taken? For now just append count if taken to be safe
                let baseName = newName
                let counter = 2
                while (userConfigs.some(c => c.name.toLowerCase() === newName.toLowerCase())) {
                    newName = `${baseName} ${counter}`
                    counter++
                }
            }
        } else {
            let baseName = `${sourceConfig.name} (Copy)`
            newName = baseName
            let counter = 2
            while (userConfigs.some(c => c.name.toLowerCase() === newName.toLowerCase())) {
                newName = `${baseName} ${counter}`
                counter++
            }
        }

        // Ensure we copy settings but strip locked status if we want the copy to be editable?
        // Usually copies should be editable.
        const newSettings = {
            ...sourceConfig.settings,
            _meta: {
                ...(sourceConfig.settings?._meta || {}),
                isLocked: false // Reset lock on copy
            }
        }

        try {
            const body = {
                name: newName,
                settings: newSettings
            }
            await storageService.saveConfig(body)

            await fetchUserConfigs(true)
            syncConfigsToGame()
        } catch (err) {
            console.error('Duplicate failed', err)
        }
    }

    const syncConfigsToGame = async (configsOverride = null) => {
        const path = cs2Path
        if (!path) return

        if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current)

        syncDebounceRef.current = setTimeout(async () => {
            setSyncStatus('syncing')

            // Safety watchdog: Force reset after 8 seconds
            const watchdog = setTimeout(() => {
                setSyncStatus('synced')
            }, 8000)

            try {
                const res = await installAllConfigsToGame(path, configsOverride)
                clearTimeout(watchdog)

                if (res.success) {
                    setSyncStatus('synced')
                    setLastSyncTime(new Date())
                } else {
                    console.error('Sync failure:', res)
                    setSyncStatus('error')
                }
            } catch (e) {
                clearTimeout(watchdog)
                console.error('Sync exception:', e)
                setSyncStatus('error')
            } finally {
                syncDebounceRef.current = null
            }
        }, 500)
    }

    const deleteConfig = async (id) => {
        try {
            // Get config name before deletion for autoexec cleanup
            const configToDelete = userConfigs.find(c => c.id === id)

            const res = await storageService.deleteConfig(id)
            if (!res.success) throw new Error(res.message || 'Failed to delete')

            if (configId === id) {
                resetConfig(null)
            }

            // Cleanup autoexec (remove bind and regenerate file)
            if (configToDelete && !configToDelete.isBuiltin) {
                await updateAutoexec({ bindsMapUpdate: { removals: [configToDelete.name] } })
            } else {
                // Even if name unknown or builtin, force autoexec refresh to clean dead binds
                // But updateAutoexec triggers sync, so we don't need explicit sync below if we call it.
                // If we didn't call updateAutoexec, we would need to manually sync.
                // Let's safe-guard: if we didn't update autoexec, we sync.
                // But wait, if we delete a config that had NO bind, we still want to remove it from the list
                // handled by updateAutoexec.
                // Actually updateAutoexec is heavy? No.
                // Let's just always call it to be safe?
                // No, only if it was a user config.
                await fetchUserConfigs(true)
                syncConfigsToGame()
            }

            return { success: true }
        } catch (error) {
            console.error('Delete failed', error)
            return { success: false, message: error.message }
        }
    }

    const getBasicBindKeys = () => [
        'key_lastinv', 'key_forward', 'key_use', 'key_reload',
        'key_left', 'key_back', 'key_right',
        'key_walk', 'key_duck',
        'key_jump', 'key_buymenu',
        'key_attack', 'key_attack2',
        'key_drop', 'key_ping', 'key_inspect',
        'key_slot1', 'key_slot2', 'key_slot3', 'key_slot4', 'key_slot5',
        'key_inv_next', 'key_inv_prev',
        'key_mic', 'key_chat', 'key_chat_team',
        'key_score', 'key_console', 'key_team_menu', 'key_show_loadout',
        'key_spray', 'key_team_equip', 'key_screenshot', 'key_quit_prompt',
        'key_sell_back_all', 'key_cancel_select'
    ]

    const getSpecialBindKeys = () => [
        'grenade_flash', 'grenade_smoke', 'grenade_molotov', 'grenade_he', 'grenade_decoy', 'grenade_binds_buy',
        'jump_throw_key', 'viewmodel_switchhands_bind',
        'mwheel_jump', 'bind_vol_cycle', 'bind_voice_cycle', 'bind_mute_all'
    ]

    const resetBindsToDefault = () => {
        // Legacy global reset (kept for safety or full reset actions)
        if (configIsLockedRef.current) {
            handleLockedAction(() => resetBindsToDefault())
            return
        }
        isDirtyRef.current = true
        setConfigData(prev => ({
            ...prev,
            binds: { ...CS2_DEFAULT_BINDS }
        }))
    }

    const resetBasicBinds = () => {
        if (configIsLockedRef.current) {
            handleLockedAction(() => resetBasicBinds())
            return
        }
        isDirtyRef.current = true
        const keysToReset = getBasicBindKeys()
        setConfigData(prev => {
            const newBinds = { ...prev.binds }
            keysToReset.forEach(k => {
                if (CS2_DEFAULT_BINDS[k] !== undefined) {
                    newBinds[k] = CS2_DEFAULT_BINDS[k]
                }
            })
            return { ...prev, binds: newBinds }
        })
    }

    const resetSpecialBinds = () => {
        if (configIsLockedRef.current) {
            handleLockedAction(() => resetSpecialBinds())
            return
        }
        isDirtyRef.current = true
        const keysToReset = getSpecialBindKeys()
        setConfigData(prev => {
            const newBinds = { ...prev.binds }
            keysToReset.forEach(k => {
                if (CS2_DEFAULT_BINDS[k] !== undefined) {
                    newBinds[k] = CS2_DEFAULT_BINDS[k]
                }
            })
            return { ...prev, binds: newBinds }
        })
    }

    const resetCustomBinds = () => {
        if (configIsLockedRef.current) {
            handleLockedAction(() => resetCustomBinds())
            return
        }
        isDirtyRef.current = true
        setConfigData(prev => ({
            ...prev,
            binds: {
                ...prev.binds,
                custom_binds: CS2_DEFAULT_BINDS.custom_binds ? [...CS2_DEFAULT_BINDS.custom_binds] : []
            }
        }))
    }

    const clearAllBinds = () => {
        // Global clear
        if (configIsLockedRef.current) {
            handleLockedAction(() => clearAllBinds())
            return
        }
        isDirtyRef.current = true
        const emptyBinds = Object.keys(DEFAULT_CONFIG_DATA.binds).reduce((acc, key) => {
            const val = DEFAULT_CONFIG_DATA.binds[key]
            acc[key] = typeof val === 'boolean' ? false : ''
            return acc
        }, {})

        setConfigData(prev => ({
            ...prev,
            binds: emptyBinds
        }))
    }

    const clearBasicBinds = () => {
        if (configIsLockedRef.current) {
            handleLockedAction(() => clearBasicBinds())
            return
        }
        isDirtyRef.current = true
        const keysToClear = getBasicBindKeys()
        setConfigData(prev => {
            const newBinds = { ...prev.binds }
            keysToClear.forEach(k => {
                newBinds[k] = ''
            })
            return { ...prev, binds: newBinds }
        })
    }

    const clearSpecialBinds = () => {
        if (configIsLockedRef.current) {
            handleLockedAction(() => clearSpecialBinds())
            return
        }
        isDirtyRef.current = true
        const keysToClear = getSpecialBindKeys()
        setConfigData(prev => {
            const newBinds = { ...prev.binds }
            keysToClear.forEach(k => {
                // Some special values might differ per type (boolean/string)
                if (typeof CS2_DEFAULT_BINDS[k] === 'boolean') {
                    newBinds[k] = false
                } else if (k === 'mwheel_jump') {
                    newBinds[k] = 'none'
                } else {
                    newBinds[k] = ''
                }
            })
            return { ...prev, binds: newBinds }
        })
    }

    const clearCustomBinds = () => {
        if (configIsLockedRef.current) {
            handleLockedAction(() => clearCustomBinds())
            return
        }
        isDirtyRef.current = true
        setConfigData(prev => ({
            ...prev,
            binds: { ...prev.binds, custom_binds: [] }
        }))
    }

    const applyPreset = (presetName, skipConfirm = false) => {
        const preset = PRO_PRESETS[presetName]
        if (!preset) return

        if (configIsLockedRef.current) {
            handleLockedAction(() => applyPreset(presetName, skipConfirm))
            return
        }

        if (skipConfirm || window.confirm(`¿Aplicar la configuración de botones de ${presetName}? Esto reemplazará tus binds actuales.`)) {
            if (!isLoadingRef.current) isDirtyRef.current = true
            track('preset_use', { kind: 'binds', id: presetName })
            setConfigData(prev => ({
                ...prev,
                binds: { ...preset }
            }))
        }
    }

    const handleLockedAction = (pendingAction) => {
        setLockedFeedbackSignal(prev => prev + 1)
    }

    const updateConfig = (moduleName, key, value, bypassLock = false) => {
        if (value === undefined) return
        if (configIsLockedRef.current && !bypassLock) {
            handleLockedAction(() => updateConfig(moduleName, key, value))
            return
        }
        if (!isLoadingRef.current) {
            isDirtyRef.current = true
            trackModuleEdit(moduleName)
        }
        setConfigData(prev => ({
            ...prev,
            [moduleName]: {
                ...prev[moduleName],
                [key]: value
            }
        }))
    }

    const updateConfigBatch = (moduleName, updates, bypassLock = false) => {
        if (!updates || Object.keys(updates).length === 0) return
        if (configIsLockedRef.current && !bypassLock) {
            handleLockedAction(() => updateConfigBatch(moduleName, updates))
            return
        }
        if (!isLoadingRef.current) {
            isDirtyRef.current = true
            trackModuleEdit(moduleName)
        }
        setConfigData(prev => ({
            ...prev,
            [moduleName]: {
                ...prev[moduleName],
                ...updates
            }
        }))
    }

    // Clears a bind field given its fieldPath (e.g. "crosshair.bind_toggle_presets").
    // Handles the custom_binds array case ("binds.custom_binds.N.key").
    const clearBind = (fieldPath) => {
        const parts = fieldPath.split('.')
        if (parts.length === 2) {
            const [module, field] = parts
            updateConfig(module, field, '', true)
        } else if (parts.length === 4 && parts[1] === 'custom_binds' && parts[3] === 'key') {
            const index = parseInt(parts[2], 10)
            setConfigData(prev => {
                const newBinds = prev.binds.custom_binds.map((b, i) =>
                    i === index ? { ...b, key: '' } : b
                )
                return { ...prev, binds: { ...prev.binds, custom_binds: newBinds } }
            })
            isDirtyRef.current = true
        }
    }

    const updateConfigGlobal = (globalUpdates, bypassLock = false) => {
        if (!globalUpdates || Object.keys(globalUpdates).length === 0) return
        if (configIsLockedRef.current && !bypassLock) {
            handleLockedAction(() => updateConfigGlobal(globalUpdates))
            return
        }
        if (!isLoadingRef.current) {
            isDirtyRef.current = true
            Object.keys(globalUpdates).forEach(moduleName => trackModuleEdit(moduleName))
        }
        setConfigData(prev => {
            const newState = { ...prev }
            Object.keys(globalUpdates).forEach(moduleName => {
                if (newState[moduleName]) {
                    newState[moduleName] = {
                        ...newState[moduleName],
                        ...globalUpdates[moduleName]
                    }
                }
            })
            return newState
        })
    }

    // Generate the final .cfg string


    const generateConfigString = (options = { format: 'raw' }, dataOverride = null) => {
        // Use provided data or fallback to state
        const data = dataOverride || configData
        // Use provided modules or fallback to state
        const modules = dataOverride ? (data._meta?.activeModules || []) : activeModules

        return generateConfigStringUtil(data, modules, options)
    }

    const getModuleCommands = (moduleName) => {
        let commands = []

        if (moduleName === 'mouse') {
            commands.push(`sensitivity "${configData.mouse.sensitivity}"`)
            commands.push(`zoom_sensitivity_ratio "${configData.mouse.zoom_sensitivity_ratio}"`)
            commands.push(`m_rawinput "${configData.mouse.m_rawinput ? '1' : '0'}"`)
            commands.push(`m_yaw "${configData.mouse.m_yaw}"`)
            commands.push(`m_pitch "${configData.mouse.m_pitch}"`)
        } else if (moduleName === 'crosshair') {
            const ch = configData.crosshair
            const add = (k, v) => commands.push(`${k} "${v}"`)

            add('cl_crosshairstyle', ch.cl_crosshairstyle)
            add('cl_crosshairsize', ch.cl_crosshairsize)
            add('cl_crosshairthickness', ch.cl_crosshairthickness)
            add('cl_crosshairgap', ch.cl_crosshairgap)
            add('cl_crosshairdot', ch.cl_crosshairdot ? '1' : '0')

            add('cl_crosshaircolor_r', ch.cl_crosshaircolor_r)
            add('cl_crosshaircolor_g', ch.cl_crosshaircolor_g)
            add('cl_crosshaircolor_b', ch.cl_crosshaircolor_b)
            add('cl_crosshairalpha', ch.cl_crosshairalpha)

            add('cl_crosshair_drawoutline', ch.cl_crosshair_drawoutline ? '1' : '0')
            add('cl_crosshair_outlinethickness', ch.cl_crosshair_outlinethickness)

            add('cl_crosshair_recoil', ch.cl_crosshair_recoil ? '1' : '0')
            add('cl_crosshair_t', ch.cl_crosshair_t ? '1' : '0')
            add('cl_crosshair_friendly_warning', ch.cl_crosshair_friendly_warning)

            add('cl_crosshair_sniper_width', ch.cl_crosshair_sniper_width || 1)

            // Crosshair Presets & Toggle
            if (ch.presets && ch.presets.length > 0) { // Changed condition to check > 0 to always include aliases if presets exist
                commands.push('\n// --- CROSSHAIR PRESETS & TOGGLE ---')

                // Helper to sanitize alias names
                const sanitize = (name) => name.replace(/[^a-zA-Z0-9_]/g, '_').replace(/_+/g, '_')

                // 1. Generate Alias for EACH preset
                const presetAliases = []
                ch.presets.forEach((preset, index) => {
                    const cleanCmds = preset.commands.replace(/[\n\r]/g, '').trim()

                    // Use preset name if available, else index
                    let aliasName = `crosshair_${index + 1}`
                    if (preset.name && preset.name.trim()) {
                        aliasName = `crosshair_${sanitize(preset.name.trim())}`
                    }

                    // Check for duplicate alias names and fallback to index if needed (simple check)
                    if (presetAliases.includes(aliasName)) {
                        aliasName = `crosshair_${index + 1}_${Date.now().toString().slice(-4)}`
                    }
                    presetAliases.push(aliasName)

                    commands.push(`alias "${aliasName}" "${cleanCmds}"`)
                })

                // 2. Generate Toggle Logic (Cycle) if more than 1 preset AND bind is set
                if (ch.presets.length > 1 && ch.bind_toggle_presets) {
                    commands.push('// Toggle Logic')
                    presetAliases.forEach((alias, index) => {
                        const nextIndex = (index + 1) % presetAliases.length
                        const nextAlias = presetAliases[nextIndex]

                        // Define a toggle step alias that applies current crosshair AND prepares the switch for the next one
                        // alias "switch_to_crosshair_1" "crosshair_1; alias toggle_crosshair switch_to_crosshair_2"
                        const switchAlias = `switch_to_${alias}`
                        commands.push(`alias "${switchAlias}" "${alias}; alias toggle_crosshair switch_to_${nextAlias}"`)
                    })

                    // Initialize
                    const firstSwitch = `switch_to_${presetAliases[0]}`
                    commands.push(`alias "toggle_crosshair" "${firstSwitch}"`) // First valid state needs to be set, usually auto-exec or manual init

                    // Actually, for the bind to work immediately and cleanly:
                    // bind "KEY" "toggle_crosshair"
                    // And "toggle_crosshair" needs to be an alias that *executes the next change*.
                    // Let's refine based on standard patterns:
                    // alias "crosshair_cycle" "ch1"
                    // alias "ch1" "crosshair_Alpha; alias crosshair_cycle ch2"
                    // alias "ch2" "crosshair_Beta; alias crosshair_cycle ch1"

                    // Re-generating toggle logic with cleaner cycle pattern
                    presetAliases.forEach((currentAlias, index) => {
                        const nextIndex = (index + 1) % presetAliases.length
                        const nextAlias = presetAliases[nextIndex]
                        const applyCurrent = `apply_${currentAlias}`

                        // apply_crosshair_Alpha -> executes crosshair_Alpha commands AND sets toggle_crosshair to point to apply_crosshair_Beta
                        commands.push(`alias "${applyCurrent}" "${currentAlias}; alias toggle_crosshair apply_${nextAlias}"`)
                    })

                    // Initial State: Point 'toggle_crosshair' to the SECOND one (assuming first is loaded by default? No, logic assumes we switch TO the next)
                    // Or simpler: Just set it to start cycling from index 1 (second preset) if 0 is active, 
                    // but we don't know what's active. Safe bet: Start cycle at 0 (first preset).
                    commands.push(`alias "toggle_crosshair" "apply_${presetAliases[1] || presetAliases[0]}"`) // Start by switching to 2nd, or 1st if only 1 (irrelevant logic if length > 1)

                    commands.push(`bind "${ch.bind_toggle_presets}" "toggle_crosshair"`)
                }

                commands.push('// ----------------------------------\n')
            }
        } else if (moduleName === 'viewmodel') {
            const vm = configData.viewmodel
            commands.push(`viewmodel_offset_x "${vm.viewmodel_offset_x}"`)
            commands.push(`viewmodel_offset_y "${vm.viewmodel_offset_y}"`)
            commands.push(`viewmodel_offset_z "${vm.viewmodel_offset_z}"`)
            commands.push(`viewmodel_fov "${vm.viewmodel_fov}"`)
            commands.push(`viewmodel_presetpos "0"`)
            commands.push(`cl_prefer_lefthanded "${vm.cl_prefer_lefthanded}"`)
        } else if (moduleName === 'hud') {
            const h = configData.hud
            commands.push(`cl_hud_color "${h.cl_hud_color || 1}"`)
            commands.push(`hud_scaling "${h.hud_scaling || 0.9}"`)
            commands.push(`cl_radar_scale "${h.cl_radar_scale || 0.7}"`)
            commands.push(`cl_hud_radar_scale "${h.cl_hud_radar_scale || 1.0}"`)
            commands.push(`cl_radar_always_centered "${h.cl_radar_always_centered ? '1' : '0'}"`)
            commands.push(`cl_radar_rotate "${h.cl_radar_rotate ? '1' : '0'}"`)
            commands.push(`cl_showloadout "${h.cl_showloadout ? '1' : '0'}"`)
            commands.push(`cl_radar_icon_scale_min "${h.cl_radar_icon_scale_min || 0.6}"`)
            commands.push(`cl_teammate_colors_show "${h.cl_teammate_colors_show ?? 1}"`)
            commands.push(`cl_radar_square_with_scoreboard "${h.cl_radar_square_with_scoreboard ? '1' : '0'}"`)
            commands.push(`cl_hud_radar_background_alpha "${h.cl_hud_radar_background_alpha ?? 0.6}"`)

            const b = configData.binds
            if (b && b.radar_big_key) {
                const rs = h.cl_radar_scale || 0.7
                const hrs = h.cl_hud_radar_scale || 1.0
                commands.push(`alias "+radar_big" "cl_radar_scale 0.25; cl_hud_radar_scale 1.3"`)
                commands.push(`alias "-radar_big" "cl_radar_scale ${rs}; cl_hud_radar_scale ${hrs}"`)
                commands.push(`bind "${b.radar_big_key}" "+radar_big"`)
            }
        }

        if (moduleName === 'binds' || moduleName === 'binds_basic') {
            const b = configData.binds

            const basicBindMap = {
                key_forward: '+forward', key_back: '+back', key_left: '+left', key_right: '+right',
                key_walk: '+sprint', key_duck: '+duck', key_jump: '+jump',
                key_attack: '+attack', key_attack2: '+attack2', key_reload: '+reload',
                key_use: '+use', key_drop: 'drop', key_inspect: '+lookatweapon',
                key_lastinv: 'lastinv', key_buymenu: 'buymenu',
                key_autobuy: 'autobuy', key_rebuy: 'rebuy',
                key_slot1: 'slot1', key_slot2: 'slot2', key_slot3: 'slot3',
                key_slot4: 'slot4', key_slot5: 'slot5',
                key_taser: 'slot11', key_healthshot: 'slot12',
                key_mic: '+voicerecord', key_chat: 'messagemode', key_chat_team: 'messagemode2',
                key_ping: 'player_ping',
                key_score: '+showscores', key_console: 'toggleconsole',
                key_spray: '+spray_menu', key_team_equip: '+cl_show_team_equipment',
                key_show_loadout: 'show_loadout_toggle', key_team_menu: 'teammenu',
                key_screenshot: 'jpeg', key_quit_prompt: 'cs_quit_prompt',
                key_sell_back_all: 'sellbackall',
                key_inv_next: 'invnext', key_inv_prev: 'invprev',
                key_cancel_select: 'cancelselect',
                key_radial_radio: '+radialradio', key_radial_radio2: '+radialradio2',
                viewmodel_switchhands_bind: 'switchhands',
                bind_mute_all: 'voice_modenable_toggle',
                bind_vol_up: 'incrementvar volume 0 1 0.1',
                bind_vol_down: 'incrementvar volume 0 1 -0.1',
                bind_voice_up: 'incrementvar snd_voipvolume 0 1 0.1',
                bind_voice_down: 'incrementvar snd_voipvolume 0 1 -0.1',
                bind_radar_toggle: 'toggle cl_radar_scale 0.35 1'
            }

            const grenadeBindMap = {
                grenade_flash: { slot: 'slot7', buy: 'buy flashbang' },
                grenade_smoke: { slot: 'slot8', buy: 'buy smokegrenade' },
                grenade_he: { slot: 'slot6', buy: 'buy hegrenade' },
                grenade_molotov: { slot: 'slot10', buy: 'buy molotov; buy incgrenade' },
                grenade_decoy: { slot: 'slot9', buy: 'buy decoy' }
            }

            const buyBindMap = {
                key_buy_ak_full: 'buy ak47; buy vesthelm; buy vest; buy defuser',
                key_buy_m4_full: 'buy m4a1; buy m4a1_silencer; buy vesthelm; buy vest; buy defuser',
                key_buy_awp_full: 'buy awp; buy vesthelm; buy vest; buy defuser',
                key_buy_grenades: 'buy flashbang; buy smokegrenade; buy hegrenade; buy molotov; buy incgrenade',
                key_buy_deagle: 'buy deagle; buy vest',
                key_buy_force_smg: 'buy mac10; buy mp9; buy vesthelm',
                key_buy_force_rifle: 'buy galilar; buy famas; buy vesthelm',
                key_buy_scout: 'buy ssg08; buy vesthelm',
                key_buy_xm1014: 'buy xm1014; buy vesthelm',
                key_buy_p250: 'buy p250; buy vest'
            }

            Object.entries(basicBindMap).forEach(([key, command]) => {
                if (b[key]) {
                    commands.push(`bind ${b[key]} ${command}`)
                }
            })

            // Grenade binds with optional auto-buy
            Object.entries(grenadeBindMap).forEach(([key, commands_obj]) => {
                if (b[key]) {
                    if (b.grenade_binds_buy) {
                        commands.push(`bind ${b[key]} "${commands_obj.slot}; ${commands_obj.buy}"`)
                    } else {
                        commands.push(`bind ${b[key]} ${commands_obj.slot}`)
                    }
                }
            })
        }

        if (moduleName === 'binds' || moduleName === 'binds_special') {
            const b = configData.binds
            if (b.jump_throw_key && b.jump_throw_key !== 'none') {
                commands.push(`alias +jt "+jump"; alias +ta "-attack; -attack2"; alias -jt "-jump"; bind ${b.jump_throw_key} "+jt;+ta"`)
            }
            if (b.mwheel_jump === 'both') {
                commands.push(`bind mwheelup +jump; bind mwheeldown +jump`)
            } else if (b.mwheel_jump === 'up') {
                commands.push(`bind mwheelup +jump`)
            } else if (b.mwheel_jump === 'down') {
                commands.push(`bind mwheeldown +jump`)
            }
            if (b.follow_recoil_key && b.follow_recoil_key !== 'none') {
                commands.push(`bind ${b.follow_recoil_key} "toggle cl_crosshair_recoil"`)
            }
            if (b.bomb_drop_key) {
                commands.push(`alias +dropbomb "slot3; slot5"; alias -dropbomb "drop; lastinv"; bind ${b.bomb_drop_key} +dropbomb`)
            }
        }

        if (moduleName === 'binds' || moduleName === 'binds_custom') {
            const b = configData.binds
            // --- CUSTOM BUY BINDS ---
            if (b.custom_binds && b.custom_binds.length > 0) {
                b.custom_binds.forEach((cb, index) => {
                    if (cb.key && cb.items && cb.items.length > 0) {
                        const aliasName = `custom_buy_${index + 1}`
                        const buyCommands = cb.items.map(item => `buy ${item}`).join('; ')
                        commands.push(`alias "${aliasName}" "${buyCommands}"`)
                        commands.push(`bind "${cb.key}" "${aliasName}"`)
                    }
                })
            }
        }

        if (moduleName === 'binds' || moduleName === 'radio') {
            // Radio Wheel Texts for Console Commands
            if (configData.settings) {
                const radialKeys = Object.keys(configData.settings).filter(k => k.startsWith('cl_radial_radio_tab_'))
                // Group by tab for comments
                const grouped = { 0: [], 1: [], 2: [] }
                radialKeys.forEach(key => {
                    const parts = key.split('_tab_')
                    if (parts[1]) {
                        const tabIndex = parseInt(parts[1].charAt(0))
                        if (!isNaN(tabIndex) && grouped[tabIndex]) {
                            grouped[tabIndex].push(key)
                        }
                    }
                })

                if (radialKeys.length > 0) {
                    commands.push('\n// --- RADIO WHEELS ---')
                    Object.keys(grouped).forEach(idx => {
                        const keys = grouped[idx]
                        if (keys.length > 0) {
                            commands.push(`// Radio Wheel ${parseInt(idx) + 1}`)
                            keys.forEach(k => {
                                commands.push(`${k} "${configData.settings[k]}"`)
                            })
                        }
                    })
                }
            }

            // Radio Keys (Moved here or duplicated in basic? Let's put specific radio keys here if they are part of the module)
            const b = configData.binds
            const radioKeys = {
                key_radio1: 'radio1', key_radio2: 'radio2', key_radio3: 'radio3'
            }
            Object.entries(radioKeys).forEach(([key, command]) => {
                if (b[key]) {
                    commands.push(`bind ${b[key]} ${command}`)
                }
            })
        }

        if (moduleName === 'custom_scripts') {
            // Custom scripts - split by lines and filter empty
            if (configData.custom_scripts?.raw_content) {
                const lines = configData.custom_scripts.raw_content
                    .split('\n')
                    .map(line => line.trim())
                    .filter(line => line && !line.startsWith('//'))
                if (lines.length > 0) {
                    commands.push(...lines)
                }
            }
        }

        return commands.join('; ')
    }

    const downloadConfig = () => {
        track('config_export')
        // 1. Download Current Active Config
        const rawConfigString = generateConfigString({ format: 'raw' })
        const element = document.createElement("a");
        const file = new Blob([rawConfigString], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        const filename = configName.endsWith('.cfg') ? configName : `${configName}.cfg`
        element.download = filename;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

        // 2. Also download autoexec.cfg
        // We look for autoexec content in recent list
        let autoexecContent = "// AUTO-GENERATED BY CONFIG-STRIKE - DO NOT EDIT MANUALLY\n\n"

        userConfigs.forEach(c => {
            if (c.launcherBind) {
                const baseName = c.name.replace(/\.cfg$/i, '')
                autoexecContent += `bind "${c.launcherBind}" "exec ${baseName}"\n`
            }
        })

        autoexecContent += "\nhost_writeconfig\n"

        const autoElement = document.createElement("a");
        const autoFile = new Blob([autoexecContent], { type: 'text/plain' });
        autoElement.href = URL.createObjectURL(autoFile);
        autoElement.download = "autoexec.cfg";
        document.body.appendChild(autoElement);

        // Small delay to ensure browser handles multiple downloads (increased to 500ms)
        setTimeout(() => {
            autoElement.click();
            document.body.removeChild(autoElement);
        }, 500);
    }

    const saveUserConfig = async (nameOverride) => {
        try {
            // Manual premium check [DISABLED TO FIX SAVE]
            /*
            if (user && !isPremiumEffective) {
                return { success: false, message: 'PREMIUM_REQUIRED' }
            }
            */

            // If current config is builtin or locked, we should have handled it in updateConfig
            // but as a safety measure, we check here too.
            let finalName = nameOverride || configName

            // [REFACTOR]: Name-Based Authority Check
            // Always check if this name already exists in the list to prevent duplicates or ID mismatch
            const existingConfig = userConfigs.find(c =>
                c.name.toLowerCase() === finalName.toLowerCase() && !c.isBuiltin
            )

            let actualId = configId

            if (existingConfig) {
                // If we found a config with this name...
                if (actualId && String(actualId) !== String(existingConfig.id)) {
                    // We have an ID, but it doesn't match the one found -> NAME CONFLICT (Rename to existing)
                    console.error(`Cannot save: Config name "${finalName}" is taken by ID ${existingConfig.id}`)
                    return { success: false, message: 'NAME_EXISTS' }
                }

                // If we didn't have an ID (lost state), or it matches, we attach to this existing config
                if (!actualId) {
                    actualId = existingConfig.id
                    setConfigId(existingConfig.id)
                }
            }

            const body = {
                id: actualId,
                name: finalName,
                settings: {
                    ...configDataRef.current,
                    _meta: {
                        activeModules: activeModulesRef.current,
                        isLocked: configIsLockedRef.current
                    }
                }
            }

            const result = await storageService.saveConfig(body)

            if (result.success) {
                if (result.config && result.config.id) {
                    setConfigId(result.config.id)
                }

                // AutoLoad Logic for first config
                if (!configId) {
                    const existingUserConfigs = userConfigs.filter(c => !c.isBuiltin)
                    if (existingUserConfigs.length === 0) {
                        setAutoLoad(finalName)
                    }
                }

                track('config_save')
                return { success: true, message: 'Saved', id: result.config?.id || actualId }
            } else {
                return { success: false, message: result.message }
            }

        } catch (error) {
            console.error('Save failed', error)
            return { success: false, message: 'Save Error' }
        }
    }

    const loadUserConfig = (config, immediate = false) => {
        if (!config) return

        // Prevent duplicate loads
        if (isLoadingRef.current) {
            return
        }

        // CRITICAL: Set loading flag to prevent auto-save
        isLoadingRef.current = true

        // Prevent loading system configs as active workspace
        // Check both flag and name match against known builtins
        const isBuiltinName = BUILTIN_CONFIGS.some(b => b.name.toLowerCase() === config.name.toLowerCase())
        if (config.isBuiltin || isBuiltinName) {
            console.error('System configs cannot be loaded as active workspace.')
            if (configNameRef.current === config.name) {
                setConfigName('')
                setConfigId(null)
                setActiveModules([])
            }
            return
        }

        // Check lock status
        const isBuiltin = config.isBuiltin || (config.id && String(config.id).startsWith('builtin-'))
        const isActuallyLocked = isBuiltin || !!config.isLocked
        setConfigIsLocked(isActuallyLocked)

        configNameRef.current = config.name

        // [AUTO-LOAD FIX] Trigger earlier to ensure sync happens while UI is animating
        if (!config.isBuiltin && !String(config.name).startsWith('builtin-')) {
            setAutoLoad(config.name).catch(console.error)
        }

        setFocusSignal({ id: 0, moduleKey: null, expand: true })

        if (immediate) {
            // CRITICAL: Mark as clean BEFORE any state updates to prevent auto-save from triggering
            isDirtyRef.current = false
            setHasUnsyncedChanges(false)

            setConfigName(config.name)
            setConfigId(config.id)
            let newSettings = config.settings

            // Explicitly set active modules, defaulting to empty if not found
            // This prevents "ghost" modules from previous config persisting
            const metaModules = newSettings?._meta?.activeModules
            if (metaModules) {
                setActiveModules(metaModules)
            } else {
                setActiveModules([])
            }

            if (newSettings?._meta && newSettings._meta.isLocked !== undefined) {
                setConfigIsLocked(!!newSettings._meta.isLocked)
            }

            setConfigData({ ...DEFAULT_CONFIG_DATA, ...newSettings })
            setLoadSignal(Date.now())

            // Persist Last Active Config
            localStorage.setItem('last_active_config_name', config.name);

            // Release loading lock after a short delay to ensure all state updates are processed
            // Increased to 800ms to allow full React render cycle and prevent effect race conditions
            setTimeout(() => {
                isDirtyRef.current = false // Force clean state
                isLoadingRef.current = false
                initialLoadDoneRef.current = true // NOW allow auto-saves
            }, 800)

            return
        }

        // 1. Collapse all current modules visually
        toggleAllModules(false)

        // 2. Wait for collapse animation (approx 300-400ms)
        setTimeout(() => {
            // CRITICAL: Mark as clean BEFORE any state updates to prevent auto-save from triggering
            isDirtyRef.current = false
            setHasUnsyncedChanges(false)

            setConfigName(config.name)
            setConfigId(config.id)

            let newSettings = config.settings

            // Check for _meta and restore activeModules OR reset
            const metaModules = newSettings?._meta?.activeModules
            if (metaModules) {
                setActiveModules(metaModules)
            } else {
                setActiveModules([])
            }

            // Also restore lock status from meta
            if (newSettings?._meta && newSettings._meta.isLocked !== undefined) {
                setConfigIsLocked(!!newSettings._meta.isLocked)
            }

            // Clean _meta before setting configData (optional, but good practice to keep data clean)
            // Actually we keep whole object structure usually
            setConfigData({
                ...DEFAULT_CONFIG_DATA,
                ...newSettings
            })

            // Persist Last Active Config
            localStorage.setItem('last_active_config_name', config.name);

            // Señalizar que se cargó una configuración
            setLoadSignal(Date.now())

            // 3. Expand the newly active modules after data update
            setTimeout(() => {
                toggleAllModules(true)

                // Release loading lock
                isDirtyRef.current = false // Force clean state
                isLoadingRef.current = false
                initialLoadDoneRef.current = true // NOW allow auto-saves

            }, 800)

        }, 1000)
    }

    // Generate the final .cfg string
    const getTemplateData = (templateId) => {
        const emptyBinds = Object.keys(DEFAULT_CONFIG_DATA.binds).reduce((acc, key) => {
            const val = DEFAULT_CONFIG_DATA.binds[key]
            acc[key] = typeof val === 'boolean' ? false : ''
            return acc
        }, {})

        if (templateId === 'empty') {
            return {
                ...DEFAULT_CONFIG_DATA,
                binds: emptyBinds,
                _meta: {
                    activeModules: [],
                    isLocked: false
                }
            }
        }

        // Check if it matches a Builtin Config
        const builtin = BUILTIN_CONFIGS.find(b => b.id === templateId)
        if (builtin) {
            return {
                ...builtin.settings,
                // Ensure we don't carry over locked status or system flags to the new user copy
                _meta: {
                    ...builtin.settings._meta,
                    isLocked: false,
                    isSystem: false,
                    // Ensure activeModules are copied if present
                    activeModules: builtin.settings._meta?.activeModules || []
                }
            }
        }

        // Fallback to internal default
        // Fallback to internal default
        if (templateId === 'default') {
            return {
                ...DEFAULT_CONFIG_DATA,
                binds: { ...CS2_DEFAULT_BINDS },
                _meta: {
                    // Activate standard modules for default template
                    activeModules: ['crosshair', 'mouse', 'viewmodel', 'hud', 'binds_basic', 'binds_special', 'binds_custom'],
                    isLocked: false
                }
            }
        }

        if (templateId === 'recommended') {
            return {
                ...DEFAULT_CONFIG_DATA,
                binds: { ...CS2_DEFAULT_BINDS },
                _meta: {
                    // Activate all standard modules for recommended
                    activeModules: ['crosshair', 'mouse', 'viewmodel', 'hud', 'binds_basic', 'binds_special', 'graphics', 'custom_scripts', 'video', 'practice'],
                    isLocked: false
                }
            }
        }

        return {
            ...DEFAULT_CONFIG_DATA,
            binds: emptyBinds,
            _meta: {
                activeModules: [],
                isLocked: false
            }
        }
    }

    const updateAutoexec = async ({ bindsMapUpdate, autoloadConfigUpdate }) => {
        try {
            // 1. Fetch all configs to get the latest autoexec state
            const allConfigs = await storageService.getConfigs()

            const autoexec = allConfigs.find(c => c.name === 'autoexec')

            // 2. Determine new state
            let bindsMap = autoexec?.settings?.bindsMap || {}
            let autoloadConfig = autoexec?.settings?.autoloadConfig || null

            if (bindsMapUpdate !== undefined) {
                bindsMap = { ...bindsMap, ...bindsMapUpdate.updates }
                // Remove keys if value is null/empty
                if (bindsMapUpdate.removals) {
                    bindsMapUpdate.removals.forEach(k => delete bindsMap[k])
                }
            }

            if (autoloadConfigUpdate !== undefined) {
                autoloadConfig = autoloadConfigUpdate
            }

            // 3. Generate autoexec content
            let autoexecContent = "// AUTO-GENERATED BY CONFIG-STRIKE - DO NOT EDIT MANUALLY\n\n"

            // Binds
            const allPossibleConfigs = [...BUILTIN_CONFIGS, ...allConfigs.filter(c => c.name !== 'autoexec')]

            // [FIX] Sanitize bindsMap: Remove keys for configs that no longer exist
            // This ensures we don't keep "zombie" binds in the persistence layer or generation
            Object.keys(bindsMap).forEach(configName => {
                const exists = allPossibleConfigs.some(c => c.name === configName)
                if (!exists) {
                    delete bindsMap[configName]
                }
            })

            // Sort by name for consistency
            allPossibleConfigs.sort((a, b) => a.name.localeCompare(b.name))

            allPossibleConfigs.forEach(c => {
                const b = bindsMap[c.name]
                if (b) {
                    const baseName = c.name.replace(/\.cfg$/i, '')
                    autoexecContent += `bind "${b}" "exec ${baseName}"\n`
                }
            })

            // AutoLoad
            if (autoloadConfig) {
                const baseName = autoloadConfig.replace(/\.cfg$/i, '')
                autoexecContent += `\n// AutoLoad Config\nexec "${baseName}"\n`
            }

            autoexecContent += "\nhost_writeconfig\n"

            // 4. Save autoexec.cfg
            const autoexecBody = {
                id: autoexec?.id || null, // Will generate ID if null
                name: 'autoexec',
                settings: {
                    _raw: autoexecContent,
                    bindsMap: bindsMap,
                    autoloadConfig: autoloadConfig,
                    _meta: { isLocked: true }
                }
            }

            await storageService.saveConfig(autoexecBody)

            // 5. Refresh local list so UI updates
            const freshConfigs = await fetchUserConfigs(true)

            // 6. [AUTO-SYNC] Trigger sync to CS2
            // Pass autoexec explicitly because fetchUserConfigs filters it out
            syncConfigsToGame([...freshConfigs, autoexecBody])

        } catch (error) {
            console.error('Failed to update autoexec', error)
        }
    }

    const setLauncherBind = async (config, bindKey) => {
        try {
            // 1. Update the individual config first (Source of truth for ManagerView)
            const updatedConfig = {
                ...config,
                settings: {
                    ...config.settings,
                    _meta: { ...config.settings?._meta, launcherBind: bindKey }
                }
            }
            await storageService.saveConfig(updatedConfig)

            // 2. Update the central autoexec config (Source of truth for bindsMap)
            const updates = {}
            if (bindKey) {
                updates[config.name] = bindKey
                await updateAutoexec({ bindsMapUpdate: { updates } })
            } else {
                await updateAutoexec({ bindsMapUpdate: { updates: {}, removals: [config.name] } })
            }

            // Redundant steps removed (fetch & sync are done inside updateAutoexec)

        } catch (error) {
            console.error('Failed to set launcher bind', error)
        }
    }

    const allLauncherBinds = useMemo(() => {
        const map = new Map()
        userConfigs.forEach(c => {
            if (c.launcherBind) {
                map.set(c.launcherBind, { label: `Launcher: ${c.name}` })
            }
        })
        return map
    }, [userConfigs])

    const clearLauncherBindByKey = async (key) => {
        const config = userConfigs.find(c => c.launcherBind === key)
        if (config) await setLauncherBind(config, '')
    }

    const setAutoLoad = async (configName) => {
        // configName can be null to unset
        // [FIX]: Ensure UI updates correctly by fetching configs after update
        await updateAutoexec({ autoloadConfigUpdate: configName })
        await fetchUserConfigs(true) // Silent refresh to update 'isAutoLoad' flags in UI
    }

    const installAllConfigsToGame = async (cs2Path, configsOverride = null) => {
        const configsToList = configsOverride || userConfigs
        const bridge = getBridge()

        if (!bridge?.installConfigsToGame) {
            console.error('[ConfigContext] bridge.installConfigsToGame not available')
            return { success: false, message: 'Bridge not available' }
        }

        try {
            // Generate files (Main configs + Practice)
            const configsToInstall = []

            // Helper for practice config
            const generatePracticeContent = (p) => {
                let output = '// PRACTICE CONFIG - config-strike.com\n\n'
                if (p.sv_cheats) output += 'sv_cheats 1\n'
                if (p.infinite_resources) {
                    output += 'sv_infinite_ammo 1\nmp_maxmoney 64000\nmp_startmoney 64000\nmp_afterroundmoney 64000\nmp_buytime 9999\nmp_buy_anywhere 1\n'
                }
                if (p.grenade_utils) {
                    output += 'sv_showimpacts 1\nsv_grenade_trajectory_prac_pipreview 1\nsv_grenade_trajectory_prac_trailtime 8\nammo_grenade_limit_total 6\n'
                }
                if (p.server_optimization) {
                    output += 'mp_limitteams 0\nmp_autoteambalance 0\nmp_roundtime 60\nmp_roundtime_defuse 60\nmp_freezetime 0\nmp_warmup_end\nmp_restartgame 1\n'
                }
                if (p.bot_freeze) output += 'bot_stop 1\n'
                if (p.auto_heal) output += 'sv_regeneration_force_on 1\n'
                if (p.radar_full) output += 'mp_radar_showall 1\n'
                if (p.wallbang_info) output += 'sv_showimpacts_penetration 1\n'
                if (p.auto_bhop) output += 'sv_autobunnyhopping 1\nsv_enablebunnyhopping 1\n'

                output += '\necho "PRACTICE CONFIG LOADED"\n'
                return output
            }

            configsToList.forEach(cfg => {
                // Skip builtins
                if (cfg.isBuiltin) return

                // Special handling for autoexec: 
                // We WANT to include it in the file list if it has raw content, 
                // to override whatever Electron might generate implicitly.
                if (cfg.name === 'autoexec') {
                    if (cfg.settings && cfg.settings._raw) {
                        configsToInstall.push({
                            name: 'autoexec',
                            content: cfg.settings._raw
                        })
                    }
                    return // Stop further processing for autoexec
                }

                const modules = cfg.settings?._meta?.activeModules || []
                const hasPractice = modules.includes('practice')

                // 1. Generate Main Config Content
                // Use separatePractice flag
                const content = generateConfigString({ format: 'file', separatePractice: hasPractice }, cfg.settings)

                configsToInstall.push({
                    name: cfg.name,
                    content: content
                })

                // 2. Generate Practice Config if needed
                if (hasPractice && cfg.settings.practice) {
                    const practiceContent = generatePracticeContent(cfg.settings.practice)

                    // We overwrite 'practice' if multiple configs define it.
                    // Check if already in list
                    const existing = configsToInstall.find(f => f.name === 'practice')
                    if (existing) {
                        existing.content = practiceContent
                    } else {
                        configsToInstall.push({
                            name: 'practice',
                            content: practiceContent
                        })
                    }
                }
            })

            // 2. Prepare Autoexec Data
            const autoexec = configsToList.find(c => c.name === 'autoexec')
            const launcherBinds = {}

            // Collect launcher binds from all user configs
            // Electron logic expects { "ConfigName": "Key" } mapping
            configsToList.forEach(c => {
                if (!c.isBuiltin && c.launcherBind) {
                    launcherBinds[c.name] = c.launcherBind
                }
            })

            const autoexecData = {
                autoloadConfig: autoexec?.settings?.autoloadConfig || null,
                bindsMap: {
                    ...(autoexec?.settings?.bindsMap || {}),
                    ...launcherBinds
                }
            }

            // 3. Call the bridge (shell/native side)
            const res = await bridge.installConfigsToGame({
                cs2Path,
                configs: configsToInstall,
                autoexecData,
                currentConfigName: configName
            })
            return res

        } catch (error) {
            console.error(error)
            return { success: false, message: error.message }
        }
    }

    // Manually save to storage AND write .cfg files to CS2 folder (Electron only).
    // Called by the "Apply to CS2" button. Avoids the debounce race that plagued auto-sync.
    const manualSaveAndSync = async () => {
        if (!configName || isSyncingRef.current) return
        isSyncingRef.current = true
        track('config_apply')
        try {
            setIsSaving(true)
            isDirtyRef.current = false

            const result = await saveUserConfig(configName)
            if (!result || !result.success) {
                isDirtyRef.current = true
                return
            }
            if (result.id) {
                setConfigId(result.id)
            }

            if (cs2Path) {
                setSyncStatus('syncing')
                const watchdog = setTimeout(() => setSyncStatus('synced'), 8000)
                try {
                    const freshConfigs = await fetchUserConfigs(true)
                    const res = await installAllConfigsToGame(cs2Path, freshConfigs)
                    clearTimeout(watchdog)
                    if (res.success) {
                        setSyncStatus('synced')
                        setLastSyncTime(new Date())
                        setHasUnsyncedChanges(false)
                    } else {
                        console.error('Manual sync failure:', res)
                        setSyncStatus('error')
                    }
                } catch (e) {
                    clearTimeout(watchdog)
                    console.error('Manual sync error:', e)
                    setSyncStatus('error')
                }
            } else {
                setHasUnsyncedChanges(false)
            }
        } catch (err) {
            isDirtyRef.current = true
            console.error('manualSaveAndSync error:', err)
        } finally {
            isSyncingRef.current = false
            setTimeout(() => setIsSaving(false), 500)
        }
    }

    return (
        <ConfigContext.Provider value={{
            activeModules,
            configData,
            configName, // Exposed
            configId,   // Exposed
            userConfigs, // Exposed list
            isLoadingConfigs, // Exposed loading state
            fetchUserConfigs, // Exposed fetcher
            loadUserConfig,   // Exposed loader
            loadSignal,       // Exposed signal
            setConfigName,
            toggleModule,
            activateModules,
            updateConfig,
            updateConfigBatch,
            updateConfigGlobal,
            generateConfigString,
            downloadConfig,
            duplicateConfig,
            deleteConfig,
            saveUserConfig,
            loadUserConfig,
            resetConfig,
            toggleAllModules,
            toggleSignal,
            focusModule,
            clearFocusSignal,
            focusSignal,
            getModuleCommands,
            resetBindsToDefault,
            clearAllBinds,
            resetBasicBinds,
            clearBasicBinds,
            resetSpecialBinds,
            clearSpecialBinds,
            resetCustomBinds,
            clearCustomBinds,
            updateConfigBatch,
            lockedFeedbackSignal,
            handleLockedAction,
            applyPreset,
            isSaving,
            hasUnsyncedChanges,
            manualSaveAndSync,
            configIsLocked,
            setConfigIsLocked,
            setLauncherBind,
            setAutoLoad,
            getTemplateData,
            installAllConfigsToGame,
            syncConfigsToGame,
            syncStatus,
            lastSyncTime,
            cs2Path,
            isDetecting,
            handleDetectPath,
            handleManualPathSelect,
            clearBind,
            allLauncherBinds,
            clearLauncherBindByKey
        }}>
            {children}
        </ConfigContext.Provider>
    )
}
